export type IncidentStatus = 'draft' | 'submitted' | 'reviewed'

export type IncidentType =
  | 'equipment_damage'
  | 'material_damage'
  | 'property_damage'
  | 'personal_injury'
  | 'near_miss'
  | 'other'

export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  equipment_damage: 'Equipment damage',
  material_damage: 'Material / stock damage',
  property_damage: 'Property damage',
  personal_injury: 'Personal injury',
  near_miss: 'Near miss',
  other: 'Other',
}

export interface PersonInvolved {
  name: string
  role: string
}

export interface DamagedItem {
  category?: 'equipment' | 'material'
  description: string
  asset_id: string
  product_code: string
  lot_serial: string
  quantity_affected: string | number | null
  unit_of_measure: string
  estimated_damage_value: string | number | null
  // '' is the unselected UI-only state; omit the key entirely (or send
  // 'yes'/'no'/'tbd') when building the save payload — the backend
  // validator rejects '' with a 422.
  repairable?: 'yes' | 'no' | 'tbd' | ''
}

export interface IncidentReport {
  id: string
  report_number: string
  status: IncidentStatus
  date_of_report: string | null
  facility_site: string | null
  // Section 1
  date_of_incident: string | null
  time_of_incident: string | null
  reported_to_mgmt_date: string | null
  location_in_warehouse: string | null
  incident_types: IncidentType[]
  // Section 2
  persons_involved: PersonInvolved[]
  witnesses: string | null
  reported_by_name: string | null
  department_shift: string | null
  // Section 3
  damaged_items: DamagedItem[]
  // Section 4
  incident_description: string | null
  cause_factors: string | null
  // Section 5
  immediate_actions: string | null
  corrective_actions: string | null
  injuries: boolean
  medical_details: string | null
  // Section 6
  mgmt_signed_by_email: string | null
  mgmt_signed_by_name: string | null
  mgmt_signed_at: string | null
  // Cross-module linkage
  inventory_discrepancy_report_id: string | null
  // Meta
  created_by: string | null
  pdf_file_id: string | null
  created_at: string | null
  updated_at: string | null
}

export interface IncidentListResponse {
  items: IncidentReport[]
  total: number
  page: number
  limit: number
}

export interface IncidentStats {
  total: number
  by_status: Record<string, number>
  draft: number
  submitted: number
  reviewed: number
}

export interface GeneratePdfResponse {
  file_id: string
  filename: string
}

export type IncidentWrite = Omit<
  IncidentReport,
  | 'id' | 'report_number' | 'status' | 'created_by' | 'pdf_file_id' | 'created_at' | 'updated_at'
  | 'mgmt_signed_by_email' | 'mgmt_signed_by_name' | 'mgmt_signed_at'
>
